import { CLASSNAME_PREFIX } from 'base/webflow/helpers';
import {
  ComponentStyle,
  NestedNode,
  WfAsset,
  WfVariable,
} from 'base/webflow/schema';
import { styleGuide } from 'base/webflow/style_guide';
import { MarginState } from 'blocks/editor/model';
import React, { memo } from 'react';
import { BreakpointContext } from './decorators';
import webflowStyles from './webflow_base.css';

function formatClassname(name: string) {
  name = name.toLowerCase();
  // See https://university.webflow.com/lesson/web-styling-using-classes#special-characters-in-class-names
  name = name.replace(/[^a-zA-Z0-9\-_]+/g, '-');
  name = name.replace(/-+$/, '');
  return name;
}

export function getMarginClassNameReact(margin: MarginState) {
  return `${CLASSNAME_PREFIX}margin-${margin.direction} ${CLASSNAME_PREFIX}margin-${margin.size}`;
}

function selectorPart(style: ComponentStyle): string {
  let base;
  switch (style.type) {
    case 'class':
      base = '.' + CLASSNAME_PREFIX + formatClassname(style.name);
      break;
    case 'tag':
      if (style.name === 'body') {
        base = '';
      } else {
        base = style.name;
      }
      break;
  }
  switch (style.comb) {
    case '&':
      return '' + base;
    case '|':
      return ' ' + base;
    case '':
      // ' ' indicates a root style, so it shouldn't be combined with anything
      return base;
  }
}

function trasnformLess(less: string, assets: readonly WfAsset[]) {
  // Apply swatch (less variables)
  less = less.replaceAll(/@swatch_(\w+)/g, (_substring, code) => {
    return METADATA.swatches.find((s) => s.id === code)?.color || 'hotpink';
  });
  // Mock vw/vh units with CSS variables
  less = less.replaceAll(/([-\d]\d*)[lsd]?v([wh])/g, 'calc($1 * var(--v$2))');
  less = less.replaceAll(
    /([-\d]\d*)v(min|max)/g,
    'calc($1 * $2(var(--vw), var(--vh)))',
  );
  // Fixed scroll viewport (i.e. no sticky)
  less = less.replaceAll(/position:\s*sticky;/g, 'position: relative;');
  // Rare image background references
  less = less.replaceAll(/@img_(\w+)/g, (_substring, id) => {
    const asset = assets.find((a) => a._id === id);
    if (id === 'example_bg' || !asset) {
      // I'm not sure what the example_bg asset actually is.
      // Hopefully it's not important.
      return 'url("https://assets.website-files.com/624380709031623bfe4aee60/626c881e91068544651ccbd0_Placeholder%20Image-1.svg")';
    }
    return `url("https://www.relume.io/__assets/${asset.siteId}/${asset.fileName}")`;
  });
  // Support webflow variable
  less = less.replaceAll(
    /@var_([\w\d-]+)/g,
    `var(--${USER_VARIABLE_PREFIX}$1)`,
  );
  return cdnifyAssetUrls(less);
}

export const USER_VARIABLE_PREFIX = 'u-';

export function VariableDefaultsCss({
  rootClassName,
  variables,
}: {
  rootClassName: string;
  variables: readonly WfVariable[];
}) {
  const byId = new Map(variables.map((n) => [n.id, n]));

  function resolve(v: WfVariable, depth = 0): string {
    switch (v.value.type) {
      case 'color':
        return v.value.value;
      case 'length':
        return v.value.value.value + v.value.value.unit;
      case 'font-family':
        return v.value.value;
      case 'ref':
        if (depth > 20) {
          console.error('Likely circular variable reference', v);
          return ERROR_VARIABLE_VALUE[v.type];
        }
        const other = byId.get(v.value.value.variableId);
        if (!other) {
          console.error('Invalid variable reference', v);
          return ERROR_VARIABLE_VALUE[v.type];
        }
        return resolve(other, depth + 1);
    }
  }
  return (
    <style>{`.${rootClassName} {
      ${variables
        .map((v) => `--${USER_VARIABLE_PREFIX}${v.id}: ${resolve(v)};`)
        .join('\n')}
    }`}</style>
  );
}

const ERROR_VARIABLE_VALUE: Record<WfVariable['type'], string> = {
  color: 'red',
  'font-family': '"Error", monospace',
  length: '1.1337px',
};

// We always use the same style guide (breakpoints, swatches, etc.).  Might be customizable in the future.
const METADATA = styleGuide.styles.data;

type ComponentCssProps = {
  styles: readonly ComponentStyle[];
  assets: readonly WfAsset[];
  rootClassName: string;
  cacheKey?: string;
};

export const ComponentCss = memo(function _ComponentCss(
  props: ComponentCssProps,
) {
  return <style>{renderStyles(props)}</style>;
});

const renderedStylesCache = new Map<string, string>();

/** Renders webflow styles to CSS */
function renderStyles({
  styles,
  assets,
  rootClassName,
  cacheKey,
}: ComponentCssProps): string {
  if (cacheKey && renderedStylesCache.has(cacheKey)) {
    return renderedStylesCache.get(cacheKey)!;
  }

  const parentByChildId = new Map(
    styles.flatMap((parent) =>
      parent.children.map((childId) => [childId, parent]),
    ),
  );

  const result = styles
    .flatMap((style) => {
      let sel = selectorPart(style);
      let parent: ComponentStyle | undefined = style;
      while ((parent = parentByChildId.get(parent?._id))) {
        sel = selectorPart(parent) + sel;
      }

      const variants = [
        ['main', style] as const,
        ...Object.entries(style.variants),
      ];
      return variants.map(([variantName, { styleLess }]) => {
        const css = trasnformLess(styleLess, assets);
        const variant = parseVariantName(variantName);
        if (variant === 'ignore') return { css: '', sortOrder: 0 };
        return {
          css: `.${rootClassName}${variant.rootSelector} ${sel}${variant.elementSelector} { ${css} }`,
          sortOrder: variant.sortOrder,
        };
      });
    })
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((n) => n.css)
    .join('\n');

  if (cacheKey) {
    renderedStylesCache.set(cacheKey, result);
  }
  return result;
}

export function breakpointClassName(breakpoint: string) {
  return `breakpoint-${breakpoint}`;
}

function parseVariantName(name: string):
  | {
      sortOrder: number;
      rootSelector: string;
      elementSelector: string;
    }
  | 'ignore' {
  const largestMaxWidth = Math.max(
    ...Object.values(METADATA.breakpoints).map((b) => b.maxWidth || 0),
    0,
  );

  let rootSelector = '';
  let elementSelector = '';
  let sortOrder = -Infinity;
  for (const part of name.split('_')) {
    if (part === 'main') {
      // If we add a class for "main" breakpoint, it'll increase the specificity of the
      // selector more than it would in webflow.
    } else if (METADATA.breakpoints.hasOwnProperty(part)) {
      // @media queries wouldn't contribute to specificity.
      // Use :where() to zero-out the class's contribution:
      rootSelector += `:where(.${breakpointClassName(part)})`;
      // The smallest breakpoint should win over largest
      // -> it should go LAST in the CSS
      // -> -> it should have the lowest sort order
      const breakpoint = METADATA.breakpoints[part];
      // the XL or XXL breakpoints have a minWidth instead of a maxWidth.
      // We don't handle them correctly here:
      sortOrder = -(breakpoint.maxWidth ?? largestMaxWidth);
    } else if (
      part === 'hover' ||
      part === 'focus' ||
      part === 'focus-visible' ||
      part.startsWith('nth-child') ||
      part === 'first-child' ||
      part === 'last-child'
    ) {
      elementSelector += `:${part}`;
    } else if (part === 'placeholder') {
      elementSelector += `::${part}`;
    } else if (part === 'open') {
      elementSelector += `.${webflowStyles['w--open']}`;
    } else if (part === 'current') {
      elementSelector += `.${webflowStyles['w--current']}`;
    } else if (part.startsWith('redirected')) {
      // "redirected" is used on form elements (check boxes, radio buttons), where the styles
      //    are applied on a div element next to the input (i.e. "redirected")
      // We don't support those correctly.
      return 'ignore';
    } else {
      console.error('Variant contains unknown part, ignoring', part, name);
      return 'ignore';
    }
  }
  return { sortOrder, rootSelector, elementSelector };
}

export function getBreakpointForViewport(width: number): BreakpointContext {
  const breakpointsRecord = styleGuide.styles.data.breakpoints;
  const breakpoints = Object.entries(breakpointsRecord)
    // ignore XL and XXL breakpoints (they have a minWidth instead of a maxWidth)
    .filter(([k, v]) => v.maxWidth != null)
    .map(([k, v]) => ({ name: k, maxWidth: v.maxWidth! }))
    // ascending order (smallest first)
    .sort((a, b) => a.maxWidth - b.maxWidth);
  const current =
    breakpoints.findIndex((b) => width <= b.maxWidth) ?? breakpoints.length - 1;
  return {
    current: breakpoints[current].name,
    smaller: breakpoints
      .slice(0, current)
      .map((b) => b.name)
      .reverse(),
    larger: breakpoints.slice(current + 1).map((b) => b.name),
  };
}

const WEBFLOW_UPLOADS_PREFIX = 'https://uploads-ssl.webflow.com/';
const WEBFLOW_ASSETS_PREFIX = 'https://assets-global.website-files.com/';
const CDN_PREFIX = 'https://www.relume.io/__assets/';

/** If the string contains any asset urls, they'll be replaced with CDN urls */
export function cdnifyAssetUrls(data: string): string {
  return data.replaceAll(
    new RegExp(`(?:${WEBFLOW_UPLOADS_PREFIX}|${WEBFLOW_ASSETS_PREFIX})`, 'g'),
    CDN_PREFIX,
  );
}

export const isNodeAlternate = (
  node: NestedNode,
  componentStyleIdToName: Map<string, string>,
) => {
  if (node.text) return false;

  for (const classId of node.classes) {
    const className = componentStyleIdToName.get(classId);
    if (
      className === 'U_text-color-alternate' ||
      className === 'U_is-alternate'
    )
      return true;
  }

  return false;
};
